import { useApp, useUserInfo } from "@next-platform/client-common-core";
import { Spacer, Spinner } from "@nextui-org/react";
import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import PageMetaTags from "@/components/common/PageMetaTags";
import { Grid, SpicyButton, Text } from "@/components/customComponents";
import Trans from "@/components/Trans/Trans";
import useApi from "@/components/useApi";
import { ROUTES } from "@/constants";
import appLogger from "@next-platform/client-common-core/logger/appLogger";

interface ILinkSubscribeStarResponse {
  status: string;
}

const subscriptionLogger = appLogger.withTag("LinkSubscribeStar");

const handleReload = () => window.location.reload();

const LinkSubscribeStar = () => {
  const { onlyUserId: id } = useUserInfo();
  const { applicationName } = useApp();
  const { jsonApiService, callApi } = useApi<ILinkSubscribeStarResponse>();
  const [searchParams] = useSearchParams();
  const [stateLinkingMessage, setStateLinkingMessage] = useState("");
  const subscribestar_code = searchParams.get("subscribestar_code");
  const { transientId } = useParams();

  let redirect_url = `https://${window.location.hostname}/link/subscribestar`;

  if (window.location.port) {
    //for local dev, I also need the port in the redirect url and use http
    redirect_url = `http://${window.location.hostname}:${window.location.port}/link/subscribestar`;
  }

  //this is a hack since Kinde won't let us have a code parameter
  if (window.location.href.includes("?code")) {
    const url = window.location.href;
    const code = url.split("code=")[1].split("&")[0];
    window.history.pushState({}, "", `/link/subscribestar?subscribestar_code=${code}`); //we do this to avoid showing the login screen for a short time
    window.location.href = `${redirect_url}?subscribestar_code=${code}`;
  }

  const onLinkButtonPressed = useCallback(() => {
    window.location.href = `https://www.subscribestar.com/oauth2/authorize?client_id=4VAxg7kYIRne8M3p9j6URDCT2bKEQfZugvoQQ19Jgvs&scope=subscriber.read+subscriber.payments.read+user.read+user.email.read&response_type=code&redirect_uri=${redirect_url}`;
  }, [redirect_url]);

  useEffect(() => {
    if (transientId && id) {
      const load = async () => {
        await callApi(jsonApiService.fetchResource, `link/subscribestar/${transientId}`);
      };
      load();
    }
  }, [callApi, jsonApiService.fetchResource, transientId, id]);

  useEffect(() => {
    if (subscribestar_code) {
      const load = async () => {
        await callApi(jsonApiService.fetchResource, `link/subscribestar/oauth?subscribestar_code=${subscribestar_code}&redirect_url=${redirect_url}`)
          .then((response) => {
            if (response?.status) {
              setStateLinkingMessage(response?.status);
              window.history.pushState({}, "", "/link/subscribestar");
            }
          })
          .catch((error) => {
            subscriptionLogger.error(error.message);
            if (error?.message === "no active pledge found") {
              setStateLinkingMessage("invalid_pledge");
            } else {
              setStateLinkingMessage("error");
            }
            window.history.pushState({}, "", "/link/subscribestar");
          });
      };
      load();
    }
  }, [callApi, jsonApiService.fetchResource, subscribestar_code, redirect_url]);

  if (!id) {
    return <PageMetaTags canonicalUrl={ROUTES.login} />;
  }
  //for now we always show the manual linking, this part will nee dto be redone.
  if (transientId) {
    return (
      <>
        <Text h2>
          <Trans i18nKey="subscription:subscribeStar.link.title" />
        </Text>
        <Text h4>
          <Trans i18nKey="subscription:subscribeStar.link.description" values={{ appName: applicationName }} />
        </Text>
        <Text>
          <Trans i18nKey="subscription:subscribeStar.link.permissionsNote" values={{ appName: applicationName }} />
        </Text>
        <Spacer y={1} />
        <SpicyButton variant="secondary_large" color="primary_strong" onPress={onLinkButtonPressed}>
          <Trans i18nKey="subscription:subscribeStar.link.connectButton" />
        </SpicyButton>
      </>
    );
  }

  if (subscribestar_code && !stateLinkingMessage) {
    return (
      <>
        <Text h3>
          <Trans i18nKey="subscription:subscribeStar.linking.loading" />
          <Spinner />
        </Text>
      </>
    );
  }
  if (stateLinkingMessage) {
    if (stateLinkingMessage === "error") {
      return (
        <>
          <Text h3>
            <Trans i18nKey="subscription:subscribeStar.linking.error.title" />
          </Text>
          <Text h4>
            <a onClick={handleReload}>
              <Trans i18nKey="subscription:subscribeStar.linking.error.reloadLink" />
            </a>{" "}
            <Trans i18nKey="subscription:subscribeStar.linking.error.reloadSuffix" />
          </Text>
        </>
      );
    }
    if (stateLinkingMessage === "invalid_pledge") {
      return (
        <>
          <Text h3>
            <Trans i18nKey="subscription:subscribeStar.linking.invalidPledge.title" />
          </Text>
          <Text h4>
            <Trans i18nKey="subscription:subscribeStar.linking.invalidPledge.description" />
          </Text>
        </>
      );
    }
    if (stateLinkingMessage === "success") {
      return (
        <>
          <Text h1>
            <Trans i18nKey="subscription:subscribeStar.linking.success" />
          </Text>
        </>
      );
    }
  }

  return (
    <>
      <Spacer y={0.5} />
      <Text h2>
        <Trans i18nKey="subscription:subscribeStar.link.title" />
      </Text>
      <Spacer y={1} />
      <Text h4>
        <Trans i18nKey="subscription:subscribeStar.link.description" values={{ appName: applicationName }} />
      </Text>
      <Spacer y={0.5} />
      <Text>
        <Trans i18nKey="subscription:subscribeStar.link.permissionsNote" values={{ appName: applicationName }} />
      </Text>
      <Spacer y={5} />
      <Grid>
        <SpicyButton
          variant="secondary_large"
          color="primary_strong"
          className="break-words whitespace-normal h-auto py-md"
          onPress={onLinkButtonPressed}
        >
          <Trans i18nKey="subscription:subscribeStar.link.connectButton" />
        </SpicyButton>
      </Grid>
    </>
  );
};

export default LinkSubscribeStar;
